/*
 * What every composer surface is, minus the typing (F-2 §3.2): the draft the text lives in from the
 * first keystroke, the attachment tray, the refusal a send came back with, and the send itself —
 * asking, waiting for an upload, posting, failing. A surface adds a way to enter text and nothing
 * more, so the textarea and a formatted editor answer for exactly the same promises: nothing typed
 * is ever only in React state, the box clears only after the surface's promise resolved — and then
 * only of what was sent, never of what somebody typed while it went — and a message that waited for
 * its uploads is posted with them rather than without.
 *
 * The loader (`Composer.tsx`) calls it once per composer and hands the result to whichever surface
 * it shows, so the draft outlives a change of surface; a surface never calls it itself.
 *
 * One thing does leave the device on a keystroke rather than on a send: the throttled signal that
 * says somebody is writing here. It is a call and not a frame — core names no socket type — and
 * the module that owns the wire installs it (`setTypingSignal`).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { currentOutbox, type OutboxNotice } from "../drafts/outbox.ts"
import type { AttachmentRef, DraftHandle, TargetKey } from "../drafts/types.ts"
import { useDraft } from "../drafts/useDraft.ts"
import { client } from "../store/store.ts"
import { readyRefs } from "./attachments/types.ts"
import { useAttachments, type Attachments } from "./attachments/useAttachments.ts"
import { useComposerSettings, type ComposerSettings } from "./settings.ts"
import { pastedTextFile } from "./text/paste.ts"
import type { ComposerProps, SendAction, SendMenuItem } from "./types.ts"

/**
 * A27: a longer body would become a snippet, and snippets do not exist yet. The send button keeps
 * its own label and is disabled; the explanation is the sentence below the box, so the control
 * never promises something the product cannot do (W3-10b OPEN 2, ruled 2026-09-07).
 */
export const MAX_MESSAGE_CHARS = 12_000

/** One empty array for every composer that starts from none, so the seeding effect has a stable
    dependency instead of a fresh literal per render. */
const NO_ATTACHMENTS: AttachmentRef[] = []

/**
 * How often at most one composer says that somebody is writing in it. The server coalesces to the
 * same window and spends `realtime.typing.send` per connection (`internal/core/realtime/
 * ephemeral.go`), so the bootstrap's own number is the one to keep to; this is the value it has
 * when the model has not answered yet.
 */
const TYPING_INTERVAL_MS = 3000

/**
 * Who is told that somebody is writing.
 *
 * The core sees every keystroke of every surface and knows nothing about conversations, so the
 * signal leaves it as one call naming the target and the module that owns the wire decides what,
 * if anything, to do with it: a direct conversation is worth a frame, a channel is noise and an
 * edit box is not a message being written (UX Q7). Absent means nobody is told, which is what
 * every surface outside a conversation gets.
 */
export type TypingSignal = (targetKey: TargetKey) => void

let typingSignal: TypingSignal | undefined

/** Installed by the surface that can send the frame; `undefined` takes the signal away again. */
export function setTypingSignal(signal: TypingSignal | undefined): void {
	typingSignal = signal
}

/** The refusal a failed send renders: the server's own key and the arguments its text names. */
export type Failure = { key: string; args?: Record<string, string | number> }

function failureOf(err: unknown): Failure {
	if (typeof err !== "object" || err === null) return { key: "error.unknown" }
	const key =
		"key" in err && typeof err.key === "string" && err.key !== "" ? err.key : "error.unknown"
	const args = "args" in err ? (err.args as Failure["args"]) : undefined
	return args === undefined ? { key } : { key, args }
}

/**
 * What stays in the box once a send of `sent` went, when the box holds `now`.
 *
 * Text typed after Enter but before the send settled is appended to what was sent, and that tail is
 * the next message being written: it stays. A box that no longer begins with what was sent was
 * changed inside the sent text while it went; it stays whole, because a near-copy of a sent message
 * is one delete away from gone and a sentence the box threw away is not coming back.
 */
export function unsentRest(sent: string, now: string): string {
	if (now === sent) return ""
	return now.startsWith(sent) ? now.slice(sent.length) : now
}

/** What the surface may ask of the core beyond its props. */
export interface ComposerCoreOptions {
	/**
	 * Puts the caret back where somebody left it once a send settled, whether it went or was
	 * refused: the surface's handle knows what its focusable element is, the core does not.
	 */
	refocus?: () => void
}

/** The state a surface and the frame around it share. */
export interface ComposerCore {
	/** The draft this composer writes into; the surface seeds from it and flushes it on blur. */
	draft: DraftHandle
	/** The staged files, their tray and the window's drag state. */
	attachments: Attachments
	/** Whether the box takes files at all (`ComposerProps.files`). */
	takesFiles: boolean
	/** The per-person composer settings; the surface's keys read `enterSends`. */
	settings: ComposerSettings
	/** The text as the draft holds it, and the one way to change it. */
	value: string
	setValue: (v: string) => void
	/** The id of the hint under the box; the frame renders it, the surface is described by it. */
	hintId: string
	/** The id of the refusal above the hint — the "too long" line the frame renders while `tooLong`. */
	statusId: string
	/**
	 * What a surface hands to `aria-describedby`. While the message is too long the refusal comes
	 * first and the hint second: somebody who has just been told the box will not send needs the
	 * reason before the chrome that names the send key (X-A2 P4).
	 */
	describedBy: string
	disabled: boolean
	/** Tighter padding for a box inside a message row or a thread pane (F-2 §13.1). */
	compact: boolean
	/** Escape and the Cancel button; absent means this composer cannot be closed. */
	cancel: (() => void) | undefined
	/** The last refusal of a send; the next keystroke clears it. */
	failure: Failure | null
	clearFailure: () => void
	/** The body is over `MAX_MESSAGE_CHARS`: the send is refused and the footer says why. */
	tooLong: boolean
	/**
	 * Nothing to send: no text beyond whitespace and no file. It decides three things at once — the
	 * send is refused, the *Send* button is not drawn, and the hint under the box stays away — so
	 * that a box nobody has written in is one line and nothing else (X-A3 P1).
	 */
	empty: boolean
	canSend: boolean
	/** True while a send is waiting for an upload that is still running. */
	waitingForUploads: boolean
	/** Sends, or promises to when an upload is still on its way up. */
	submit: () => void
	/** The send button's words and menu, where the surface gave them (`ComposerProps.sendAction`). */
	sendAction: SendAction | undefined
	/**
	 * Sends the box's content one of the menu's ways, under the same rules as `submit`. A disabled
	 * entry sends nothing and says its `detailKey` under the box instead — the keys of an entry that
	 * cannot run must not do nothing in silence.
	 */
	submitVia: (item: SendMenuItem) => void
	/**
	 * Takes a paste that is too long for the box into the tray as a text file
	 * (`ComposerProps.longPaste`) and answers true; answers false for a paste the box should take.
	 */
	pasteLong: (text: string) => boolean
}

/**
 * Where the caret stood in the stand-in the moment the editor's chunk arrived (F-2 D6, X-A2 D6).
 *
 * The formatted editor is a lazy chunk and the source view stands in for it meanwhile. The swap is
 * lossless for the *text* by construction — both surfaces are views over one core — but not for the
 * caret: the element that had the focus leaves the document, and on a slow line the next keystrokes
 * then go nowhere. So the stand-in leaves a note and the editor restores it into the box it opens.
 *
 * The note carries the moment it was written, because what it means is "somebody was typing here a
 * moment ago". A box built seconds later — a target that changed, a view rebuilt for a reason of its
 * own — is not that person still typing, and taking the focus there would be a focus change nobody
 * asked for (WCAG 2.1.2).
 */
export interface StandIn {
	/** The caret's offset in the text, so the editor restores something better than "the end". */
	caret: number
	/** Epoch-ms of the keystroke or click this note was written for. */
	at: number
}

/** What a surface is rendered with: the consumer's props and the core the loader built over them. */
export interface ComposerSurfaceProps extends ComposerProps {
	core: ComposerCore
	/**
	 * The stand-in says where the caret is, or `null` when it no longer has it. The note belongs to
	 * the loader, which is the only thing that outlives the swap.
	 */
	noteCaret?: (state: StandIn | null) => void
	/**
	 * The editor reads that note when it opens its box. It is not consumed by the read: React's
	 * development mode builds the view, throws it away and builds it again, and a note consumed by
	 * the view that was thrown away would leave the second one with no focus at all. The editor
	 * restores it once per view instead, and the note's age is what keeps it from ever reaching a box
	 * that has nothing to do with it.
	 */
	takeCaret?: () => StandIn | null
}

export function useComposerCore(
	props: ComposerProps,
	options: ComposerCoreOptions = {},
): ComposerCore {
	const { targetKey, initialMarkdown, initialAttachments, disabled = false } = props
	const draft = useDraft(targetKey)
	const settings = useComposerSettings()
	const staged = useAttachments(targetKey, draft, initialAttachments ?? NO_ATTACHMENTS)
	const takesFiles = props.files !== false
	// A box that takes no files keeps the tray's state but drops what would enter it.
	const attachments = useMemo(
		() => (takesFiles ? staged : { ...staged, attach: () => {} }),
		[takesFiles, staged],
	)
	const [sending, setSending] = useState(false)
	const [waitingForUploads, setWaitingForUploads] = useState(false)
	const [failure, setFailure] = useState<Failure | null>(null)

	const { value } = draft

	// When this composer last said that somebody is writing in it. A ref and not state: nothing on
	// screen follows it, and a re-render per keystroke to remember a number would be one.
	const signalledAt = useRef(0)
	useEffect(() => {
		signalledAt.current = 0
	}, [targetKey])

	// The box's text as of the latest keystroke, for a send that settles after the render it started
	// in. `setValue` writes it at once, so a key pressed right after Enter is in it before the send's
	// promise resolves; the effect follows writes from outside the box (a reload, Undo, a refusal).
	const typed = useRef(value)
	useEffect(() => {
		typed.current = value
	}, [value])

	/**
	 * The one way the text changes, and with it the typing signal — at most one per interval and
	 * only while there is something in the box. An empty box is somebody who has just deleted what
	 * they wrote, and "Anna is typing …" over nothing is the indicator everybody has learned to
	 * distrust.
	 */
	const setValue = useCallback(
		(v: string) => {
			typed.current = v
			draft.setValue(v)
			if (typingSignal === undefined || v.trim() === "") return
			const now = Date.now()
			const interval = client.model.limits?.typingIntervalMs ?? TYPING_INTERVAL_MS
			if (now - signalledAt.current < interval) return
			signalledAt.current = now
			typingSignal(targetKey)
		},
		[draft, targetKey],
	)

	// An edit or a reply starts from given text — but a draft that survived a reload wins over it.
	// It writes to the draft rather than through `setValue`: seeding a box is not somebody typing.
	useEffect(() => {
		if (draft.ready && draft.recovered === null && draft.value === "" && initialMarkdown) {
			draft.setValue(initialMarkdown)
		}
		// Only when the disk has answered for this key; a later change of the seed is not a reset.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [draft.ready, targetKey])

	// What the outbox says about this target's message. A refusal: the text is back in the draft
	// and the box says why. A message taken back inside its window: the draft store has the text
	// again and the box follows it (the draft is the authority, `useDraft`), but the tray was
	// emptied when the message left, so its files are put back here — or the next send would post
	// the text without them.
	const { tray } = attachments
	useEffect(() => {
		const outbox = currentOutbox()
		if (outbox === undefined) return
		return outbox.subscribe((_items, notice?: OutboxNotice) => {
			if (notice === undefined || notice.target !== targetKey) return
			// The outbox carries the refusal's key alone; its arguments do not survive the queue.
			if (notice.kind === "refused") setFailure({ key: notice.key })
			if (notice.kind === "undone") tray.seed(notice.attachments)
		})
	}, [targetKey, tray])

	const clearFailure = useCallback(() => setFailure((last) => (last === null ? last : null)), [])

	const tooLong = value.length > MAX_MESSAGE_CHARS
	const empty = value.trim() === "" && attachments.files.length === 0
	// A file that failed to upload blocks the send until somebody retries or removes it: sending
	// would silently drop it, which is the one outcome an attachment must never have.
	const canSend = !disabled && !sending && !empty && !tooLong && !attachments.blocked

	const { refocus } = options
	const post = useCallback(
		async (send: ComposerProps["onSend"]) => {
			// The tray is asked for its files here and not read from the render that started the send:
			// a message that waited for its uploads is posted after they finished, and the snapshot the
			// click closed over still had every one of them as "uploading".
			const tray = attachments.tray
			const sent = value
			setSending(true)
			setFailure(null)
			try {
				await send(sent.replace(/\s+$/, ""), readyRefs(tray.files()))
				// The tray is emptied before the draft, and not after it: emptying the tray writes the
				// empty file list into the draft, and a draft with a write still pending refuses what
				// arrives from outside — so a message taken back out of its undo window in the next
				// moment (Ctrl+Z in the empty box) would be dropped on the way in and its text erased
				// from the disk by that pending write. Clearing the draft last leaves nothing pending,
				// and so does keeping a rest: it is written at once rather than after the debounce.
				tray.clear()
				const rest = unsentRest(sent, typed.current)
				if (rest === "") {
					await draft.clear()
				} else {
					typed.current = rest
					draft.setValue(rest)
					await draft.flush()
				}
			} catch (err) {
				setFailure(failureOf(err))
			} finally {
				setSending(false)
				refocus?.()
			}
		},
		[attachments.tray, draft, refocus, value],
	)

	/**
	 * Send, or promise to. While an upload is still running the message is not refused and the
	 * person is not made to wait for a moment they cannot see: the button says it will go when the
	 * uploads finish, and it does (spec 07 §4 "States"). The promise outlives the component — a
	 * conversation switch does not take back a send somebody already asked for.
	 */
	const sendWith = useCallback(
		(send: ComposerProps["onSend"]) => {
			if (!canSend) return
			const tray = attachments.tray
			if (!tray.busy()) {
				void post(send)
				return
			}
			setWaitingForUploads(true)
			void tray.settled().then(() => {
				setWaitingForUploads(false)
				// An upload that failed while the message was waiting takes the promise back rather than
				// sending a message without the file.
				if (!tray.blocked()) void post(send)
			})
		},
		[canSend, attachments.tray, post],
	)
	const submit = useCallback(
		() => sendWith((markdown, files) => props.onSend(markdown, files)),
		[sendWith, props],
	)
	const submitVia = useCallback(
		(item: SendMenuItem) => {
			if (item.disabled === true) {
				if (item.detailKey !== undefined) {
					setFailure({
						key: item.detailKey,
						...(item.detailArgs === undefined ? {} : { args: item.detailArgs }),
					})
				}
				return
			}
			sendWith((markdown, files) => item.run(markdown, files))
		},
		[sendWith],
	)

	const { longPaste } = props
	const pasteLong = useCallback(
		(text: string): boolean => {
			if (longPaste === undefined || text.length <= longPaste.maxInlineChars) return false
			const tray = attachments.tray
			const pasted = pastedTextFile(
				text,
				tray.files().map((file) => file.name),
				longPaste.transform,
			)
			tray.attach([pasted.file], pasted.note)
			return true
		},
		[longPaste, attachments.tray],
	)

	const hintId = `${targetKey}-hint`
	const statusId = `${targetKey}-status`

	return {
		draft,
		attachments,
		takesFiles,
		settings,
		value,
		setValue,
		hintId,
		statusId,
		describedBy: tooLong ? `${statusId} ${hintId}` : hintId,
		disabled,
		compact: props.compact ?? false,
		cancel: props.onCancel,
		failure,
		clearFailure,
		tooLong,
		empty,
		canSend,
		waitingForUploads,
		submit,
		sendAction: props.sendAction,
		submitVia,
		pasteLong,
	}
}
